import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useWS } from '../../contexts/WSContext';
import BrandIcon from '../../components/BrandIcon';
import api from '../../services/api';
import toast from 'react-hot-toast';

function playNewOrderAlarm() {
  try {
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextClass) return () => {};
    const audioContext = new AudioContextClass();
    const beeps = [];

    [0, 0.35, 0.7].forEach((startAt) => {
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(880, audioContext.currentTime + startAt);
      gainNode.gain.setValueAtTime(0.0001, audioContext.currentTime + startAt);
      gainNode.gain.exponentialRampToValueAtTime(0.22, audioContext.currentTime + startAt + 0.02);
      gainNode.gain.exponentialRampToValueAtTime(0.0001, audioContext.currentTime + startAt + 0.28);
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      oscillator.start(audioContext.currentTime + startAt);
      oscillator.stop(audioContext.currentTime + startAt + 0.3);
      beeps.push(oscillator);
    });

    return () => {
      beeps.forEach(source => {
        try { source.stop(); } catch {}
      });
      audioContext.close().catch(() => {});
    };
  } catch {
    return () => {};
  }
}

function OrderAlert({ order, onAccept, onDecline }) {
  const [timer, setTimer] = useState(30);

  useEffect(() => {
    const t = setInterval(() => {
      setTimer(prev => {
        if (prev <= 1) { clearInterval(t); onDecline(); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-end p-4">
      <div className="bg-white rounded-3xl w-full p-6 slide-up">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-black text-xl text-gray-800">Novo Pedido!</h2>
          <div className={`w-12 h-12 rounded-full flex items-center justify-center font-black text-lg ${timer <= 10 ? 'bg-red-100 text-red-600' : 'bg-orange-100 text-primary'}`}>
            {timer}s
          </div>
        </div>

        <div className="bg-gray-50 rounded-2xl p-4 mb-4 space-y-2">
          <div className="flex items-center gap-2">
            <span className="text-2xl">{order.brandEmoji}</span>
            <div>
              <div className="font-bold text-gray-800">{order.brandName}</div>
              <div className="text-sm text-gray-500">{order.sizeLabel} • {order.quantity}x</div>
            </div>
          </div>
          <div className="text-sm text-gray-600">📍 {order.address}</div>
          <div className="flex items-center justify-between pt-2 border-t border-gray-200">
            <span className="text-gray-600 text-sm">Seu ganho</span>
            <span className="text-green-600 font-black text-xl">R$ {order.deliveryPrice?.toFixed(2)}</span>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={onDecline}
            className="py-4 rounded-2xl font-bold text-gray-600 bg-gray-100 hover:bg-gray-200 transition-all active:scale-95"
          >
            Recusar
          </button>
          <button
            onClick={onAccept}
            className="py-4 rounded-2xl font-bold text-white bg-green-500 hover:bg-green-600 transition-all active:scale-95 shadow-lg shadow-green-500/30"
          >
            ✓ Aceitar
          </button>
        </div>
      </div>
    </div>
  );
}

export default function DeliveryDashboard() {
  const { user, setUser, logout } = useAuth();
  const { on, connect } = useWS();
  const nav = useNavigate();
  const [isOnline, setIsOnline] = useState(user?.isOnline || false);
  const [pendingOrder, setPendingOrder] = useState(null);
  const [recentOrders, setRecentOrders] = useState([]);
  const [earnings, setEarnings] = useState({ balance: 0, deliveriesCount: 0 });
  const [locationGranted, setLocationGranted] = useState(false);
  const togglingRef = useRef(false);
  const stopAlarmRef = useRef(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) connect(token);
  }, []);

  useEffect(() => {
    api.get('/orders/my').then(r => setRecentOrders(r.data.slice(0, 5)));
    api.get('/delivery/earnings').then(r => setEarnings(r.data));
  }, []);

  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      position => {
        setLocationGranted(true);
        api.put('/delivery/online', {
          isOnline: user?.isOnline || false,
          location: {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          },
        }).catch(() => {});
      },
      () => {
        setLocationGranted(false);
      },
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 0 },
    );
  }, []);

  const requestLocationAccess = () => {
    if (!navigator.geolocation) {
      toast.error('Seu aparelho não suporta localização');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      position => {
        setLocationGranted(true);
        api.put('/delivery/online', {
          isOnline,
          location: {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          },
        }).catch(() => {});
        toast.success('Localização liberada com sucesso');
      },
      () => {
        toast.error('É necessário liberar a localização do aparelho');
      },
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 0 },
    );
  };

  useEffect(() => {
    const off1 = on('new_order', (msg) => {
      setPendingOrder(msg.order);
      stopAlarmRef.current?.();
      stopAlarmRef.current = playNewOrderAlarm();
      // Vibrate if supported
      if (navigator.vibrate) navigator.vibrate([200, 100, 200]);
    });
    const off2 = on('order_taken', () => {
      setPendingOrder(null);
      stopAlarmRef.current?.();
      stopAlarmRef.current = null;
    });
    return () => { off1(); off2(); };
  }, [on]);

  const toggleOnline = async () => {
    if (togglingRef.current) return;
    if (!locationGranted) {
      toast.error('Libere a localização do aparelho para ficar online');
      return;
    }
    togglingRef.current = true;
    const newStatus = !isOnline;
    try {
      navigator.geolocation.getCurrentPosition(async position => {
        await api.put('/delivery/online', {
          isOnline: newStatus,
          location: {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          },
        });
      }, async () => {
        await api.put('/delivery/online', { isOnline: newStatus });
      }, { enableHighAccuracy: true, timeout: 8000, maximumAge: 0 });
      setIsOnline(newStatus);
      setUser(prev => ({ ...prev, isOnline: newStatus }));
      toast.success(newStatus ? 'Você está online! Aguardando pedidos...' : 'Você está offline');
    } catch {
      toast.error('Erro ao atualizar status');
    } finally {
      togglingRef.current = false;
    }
  };

  const acceptOrder = async () => {
    if (!pendingOrder) return;
    try {
      const r = await api.post(`/orders/${pendingOrder.id}/accept`);
      stopAlarmRef.current?.();
      stopAlarmRef.current = null;
      setPendingOrder(null);
      toast.success('Pedido aceito!');
      nav(`/entregador/entrega/${r.data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Não foi possível aceitar');
      setPendingOrder(null);
    }
  };

  useEffect(() => () => {
    stopAlarmRef.current?.();
  }, []);

  // Check active order
  useEffect(() => {
    api.get('/orders/my').then(r => {
      const active = r.data.find(o => ['accepted', 'in_transit'].includes(o.status));
      if (active) nav(`/entregador/entrega/${active.id}`);
    });
  }, []);

  return (
    <div className="screen bg-gray-900 flex flex-col text-white">
      {pendingOrder && (
        <OrderAlert
          order={pendingOrder}
          onAccept={acceptOrder}
          onDecline={() => {
            stopAlarmRef.current?.();
            stopAlarmRef.current = null;
            setPendingOrder(null);
          }}
        />
      )}

      {/* Header */}
      <div className="gradient-hero px-6 pt-10 pb-7 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-dots opacity-30 pointer-events-none" />
        <div className="relative">
          <div className="flex justify-center mb-3">
            <img src="/logo.png" alt="GasNaPorta" className="h-20 w-auto object-contain logo-glow" />
          </div>
          <div className="flex items-center justify-between mb-6">
            <div>
              <span className="text-[10px] uppercase tracking-[0.3em] text-sky-300 font-black">Entregador</span>
              <h2 className="font-black text-xl mt-0.5">{user?.name?.split(' ')[0]} 🛵</h2>
            </div>
            <button onClick={logout} className="text-sky-200 text-xs font-semibold hover:text-white">Sair</button>
          </div>

        {!locationGranted && (
          <div className="bg-amber-500/15 border border-amber-500/30 rounded-2xl p-4 mb-4">
            <div className="font-bold text-amber-300 text-sm">Localização obrigatória</div>
            <div className="text-xs text-amber-100 mt-1 mb-3">
              Para receber pedidos, o app do entregador precisa da localização do aparelho.
            </div>
            <button
              onClick={requestLocationAccess}
              className="bg-amber-400 text-gray-900 font-bold text-sm px-4 py-2 rounded-xl"
            >
              Liberar localização
            </button>
          </div>
        )}

        {/* Online toggle */}
        <div
          className={`rounded-2xl p-5 flex items-center justify-between cursor-pointer transition-all ${isOnline ? 'bg-green-600' : 'bg-gray-700'}`}
          onClick={toggleOnline}
        >
          <div>
            <div className="font-black text-lg">{isOnline ? 'Online ✓' : 'Offline'}</div>
            <div className="text-sm opacity-80">{isOnline ? 'Recebendo pedidos' : 'Toque para ficar online'}</div>
          </div>
          <div className="flex -space-x-2">
            {(user?.brands || []).slice(0, 3).map(brandId => (
              <div key={brandId} className="w-10 h-10 rounded-full bg-white flex items-center justify-center border-2 border-white overflow-hidden">
                <BrandIcon brandId={brandId} size={26} />
              </div>
            ))}
          </div>
          <div className={`w-14 h-8 rounded-full relative transition-all ${isOnline ? 'bg-green-400' : 'bg-gray-600'}`}>
            <div className={`absolute top-1 w-6 h-6 rounded-full bg-white transition-all shadow ${isOnline ? 'left-7' : 'left-1'}`} />
          </div>
        </div>
        </div>
      </div>

      {/* Stats */}
      <div className="px-6 grid grid-cols-2 gap-3 mb-6">
        <div className="bg-gray-800 rounded-2xl p-4">
          <div className="text-gray-400 text-xs mb-1">Saldo disponível</div>
          <div className="text-green-400 font-black text-xl">R$ {earnings.balance?.toFixed(2)}</div>
        </div>
        <div className="bg-gray-800 rounded-2xl p-4">
          <div className="text-gray-400 text-xs mb-1">Avaliação média</div>
          <div className="text-primary font-black text-xl">⭐ {(user?.rating || 5).toFixed(1)}</div>
        </div>
      </div>

      {/* Nav buttons */}
      <div className="px-6 grid grid-cols-2 gap-3 mb-6">
        <button
          onClick={() => nav('/entregador/ganhos')}
          className="bg-gray-800 rounded-2xl p-4 text-center hover:bg-gray-700 transition-all active:scale-95"
        >
          <div className="text-2xl mb-1">💰</div>
          <div className="text-xs font-semibold">Meus Ganhos</div>
        </button>
        <button
          onClick={() => api.get('/orders/available').then(r => {
            if (r.data.length > 0) setPendingOrder(r.data[0]);
            else toast('Nenhum pedido disponível agora');
          })}
          className="bg-gray-800 rounded-2xl p-4 text-center hover:bg-gray-700 transition-all active:scale-95"
        >
          <div className="text-2xl mb-1">🔍</div>
          <div className="text-xs font-semibold">Ver Disponíveis</div>
        </button>
      </div>

      <div className="px-6 mb-6">
        <button
          onClick={() => nav('/entregador/marcas')}
          className="w-full bg-gray-800 rounded-2xl p-4 flex items-center justify-between hover:bg-gray-700 transition-all active:scale-95"
        >
          <div className="text-left">
            <div className="font-bold text-sm">Marcas que eu trabalho</div>
            <div className="text-xs text-gray-400">Adicionar ou remover marcas</div>
          </div>
          <div className="text-xl">🏷️</div>
        </button>
      </div>

      {/* Recent orders */}
      <div className="px-6 flex-1 overflow-y-auto">
        <h3 className="font-bold text-gray-300 text-sm mb-3">Histórico recente</h3>
        {recentOrders.length === 0 && (
          <div className="text-center py-8 text-gray-600">
            <p className="text-4xl mb-2">📦</p>
            <p className="text-sm">Nenhuma entrega ainda</p>
          </div>
        )}
        <div className="space-y-2 pb-6">
          {recentOrders.map(o => (
            <div key={o.id} className="bg-gray-800 rounded-xl p-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span>{o.brandEmoji}</span>
                <div>
                  <div className="text-sm font-semibold">{o.brandName} {o.sizeLabel}</div>
                  <div className="text-xs text-gray-400">{new Date(o.createdAt).toLocaleDateString('pt-BR')}</div>
                </div>
              </div>
              <div className={`text-sm font-bold ${o.status === 'delivered' ? 'text-green-400' : 'text-gray-400'}`}>
                {o.status === 'delivered' ? `+R$ ${o.deliveryPrice?.toFixed(2)}` : o.status}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
